/**
 * WSH DAW Collection Sessions Video — JS
 * Handles: genre pill click, dot click, auto-advance on video end,
 * IntersectionObserver for preload upgrade, label band updates.
 * Idempotent: safe to call init() multiple times (re-render resilient).
 */
(function () {
  'use strict';

  var SECTION_SEL = '[data-wsh-section="daw-collection-sessions-video"]';

  // Media coordinator integration (matches Brief 5 event contract).
  // Bound once at module scope (survives section re-render) — pauses all videos
  // in this section when another media surface takes control.
  function pauseAllInSectionVideos() {
    var section = document.querySelector(SECTION_SEL);
    if (!section) return;
    var videos = section.querySelectorAll('video');
    for (var i = 0; i < videos.length; i++) {
      if (!videos[i].paused) videos[i].pause();
    }
    var activeWrap = section.querySelector('.wsh-col-video__video-wrap.is-playing');
    if (activeWrap) activeWrap.classList.remove('is-playing');
  }

  document.addEventListener('videos:pause', pauseAllInSectionVideos);
  document.addEventListener('wsh:video:play', function (e) {
    if (e && e.detail && e.detail.source !== 'daw-collection-sessions-video') {
      pauseAllInSectionVideos();
    }
  });

  function init() {
    var section = document.querySelector(SECTION_SEL);
    if (!section) return;

    if (section.dataset.wshInited === 'true') return;
    section.dataset.wshInited = 'true';

    var pills = section.querySelectorAll('.wsh-col-video__pill');
    var videoWraps = section.querySelectorAll('.wsh-col-video__video-wrap');
    var dotsContainer = section.querySelector('[data-role="dots"]');
    var labelProductTitle = section.querySelector('[data-role="product-title"]');
    var labelCounter = section.querySelector('[data-role="counter"]');
    var shopCta = section.querySelector('[data-role="shop-cta"]');
    var ctaTitle = section.querySelector('[data-role="cta-title"]');

    if (!pills.length || !videoWraps.length) return;

    // Get the active genre on init
    var activeGenre = '';
    var activePill = section.querySelector('.wsh-col-video__pill--active');
    if (activePill) {
      activeGenre = activePill.dataset.genre;
    }

    function getWrapsForGenre(genre) {
      return section.querySelectorAll('.wsh-col-video__video-wrap[data-genre="' + genre + '"]');
    }

    function pauseAllVideos() {
      var videos = section.querySelectorAll('video');
      for (var i = 0; i < videos.length; i++) {
        if (!videos[i].paused) videos[i].pause();
      }
    }

    function hideAllWraps() {
      for (var i = 0; i < videoWraps.length; i++) {
        videoWraps[i].dataset.active = 'false';
        videoWraps[i].style.display = 'none';
        videoWraps[i].classList.remove('wsh-col-video__video-wrap--active');
      }
    }

    function showWrap(wrap) {
      wrap.dataset.active = 'true';
      wrap.style.display = '';
      wrap.classList.add('wsh-col-video__video-wrap--active');

      // Upgrade preload if in viewport
      if (section.dataset.wshInView === 'true') {
        var video = wrap.querySelector('video');
        if (video && video.preload === 'none') {
          video.preload = 'metadata';
        }
      }
    }

    function updateLabelBand(wrap) {
      var title = wrap.dataset.productTitle || '';
      var position = parseInt(wrap.dataset.position, 10) + 1;
      var total = parseInt(wrap.dataset.total, 10);
      var url = wrap.dataset.productUrl || '#';
      var thumbUrl = wrap.dataset.productThumb || '';

      if (labelProductTitle) labelProductTitle.textContent = title;
      if (labelCounter) labelCounter.textContent = ' \u00B7 ' + position + ' of ' + total;
      if (shopCta) shopCta.href = url;
      if (ctaTitle) ctaTitle.textContent = title;

      // Update thumbnail
      var thumbEl = section.querySelector('[data-role="product-thumb"]');
      var thumbLink = section.querySelector('[data-role="thumb-link"]');
      if (thumbEl) {
        if (thumbUrl) {
          thumbEl.src = thumbUrl;
          thumbEl.style.display = '';
          if (thumbLink) thumbLink.style.display = '';
        } else {
          thumbEl.style.display = 'none';
          if (thumbLink) thumbLink.style.display = '';
        }
      }
      // Update fallback block visibility
      var fallbackEl = section.querySelector('[data-role="thumb-fallback"]');
      if (fallbackEl) {
        fallbackEl.style.display = thumbUrl ? 'none' : '';
      }
      if (thumbLink) thumbLink.href = url;
    }

    function renderDots(genre) {
      if (!dotsContainer) return;
      var wraps = getWrapsForGenre(genre);
      var html = '';
      for (var i = 0; i < wraps.length; i++) {
        var isActive = wraps[i].dataset.active === 'true';
        html += '<button class="wsh-col-video__dot' + (isActive ? ' wsh-col-video__dot--active' : '') + '" data-genre="' + genre + '" data-position="' + i + '" aria-label="Video ' + (i + 1) + '">';
        if (isActive) {
          html += '<span class="wsh-col-video__dot-counter">' + (i + 1) + ' of ' + wraps.length + '</span>';
        }
        html += '</button>';
      }
      dotsContainer.innerHTML = html;

      // Bind dot clicks
      var dots = dotsContainer.querySelectorAll('.wsh-col-video__dot');
      for (var d = 0; d < dots.length; d++) {
        dots[d].addEventListener('click', onDotClick);
      }
    }

    function switchToPosition(genre, position) {
      pauseAllVideos();
      hideAllWraps();
      var wraps = getWrapsForGenre(genre);
      if (position < wraps.length) {
        showWrap(wraps[position]);
        updateLabelBand(wraps[position]);
      }
      renderDots(genre);
    }

    function switchGenre(genre) {
      activeGenre = genre;

      // Update pill states
      for (var p = 0; p < pills.length; p++) {
        var isActive = pills[p].dataset.genre === genre;
        pills[p].classList.toggle('wsh-col-video__pill--active', isActive);
        pills[p].setAttribute('aria-pressed', isActive ? 'true' : 'false');
      }

      // Show first video of the new genre
      switchToPosition(genre, 0);
    }

    // Pill click handler
    for (var i = 0; i < pills.length; i++) {
      pills[i].addEventListener('click', function () {
        switchGenre(this.dataset.genre);
      });
    }

    // Dot click handler
    function onDotClick() {
      var genre = this.dataset.genre;
      var position = parseInt(this.dataset.position, 10);
      switchToPosition(genre, position);
    }

    // --- Custom video player (no native controls) ---
    // Play button overlay visibility is CSS-driven via .is-playing class on wrap.
    var allVideos = section.querySelectorAll('video');

    for (var v = 0; v < allVideos.length; v++) {
      (function (video) {
        var wrap = video.closest('.wsh-col-video__video-wrap');
        if (!wrap) return;

        // Remove native controls entirely
        video.removeAttribute('controls');

        // Click on wrap (video or play button overlay) toggles play/pause
        wrap.addEventListener('click', function (e) {
          // Don't toggle if clicking the label band or other non-player elements
          if (e.target.closest('.wsh-col-video__label-band')) return;
          if (video.paused) {
            video.play().catch(function () {});
          } else {
            video.pause();
          }
        });

        // Sync is-playing class with actual video state
        video.addEventListener('play', function () {
          wrap.classList.add('is-playing');
          // Media coordinator: signal other surfaces to yield.
          document.dispatchEvent(new CustomEvent('wsh:video:play', {
            detail: { source: 'daw-collection-sessions-video' }
          }));
          // Belt-and-braces: also pause audio manager directly.
          var mgr = document.querySelector('wsh-audio-manager');
          if (mgr && typeof mgr.pause === 'function') mgr.pause();
        });
        video.addEventListener('pause', function () {
          wrap.classList.remove('is-playing');
        });
        video.addEventListener('ended', function () {
          wrap.classList.remove('is-playing');
        });
      })(allVideos[v]);
    }

    // Auto-advance on video end — bound to every <video> element once at init.
    // The ended event does not bubble, so delegation on the section won't work.
    for (var v2 = 0; v2 < allVideos.length; v2++) {
      allVideos[v2].addEventListener('ended', function () {
        var wrap = this.closest('.wsh-col-video__video-wrap');
        if (!wrap || wrap.dataset.active !== 'true') return;
        var genre = wrap.dataset.genre;
        var position = parseInt(wrap.dataset.position, 10);
        var total = parseInt(wrap.dataset.total, 10);
        if (position < total - 1) {
          switchToPosition(genre, position + 1);
          // Auto-play the next video
          var nextWrap = section.querySelector('.wsh-col-video__video-wrap[data-active="true"]');
          if (nextWrap) {
            var nextVideo = nextWrap.querySelector('video');
            if (nextVideo) {
              nextVideo.play().catch(function () {});
            }
          }
        }
      });
    }

    // IntersectionObserver: upgrade active video preload on viewport entry
    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        for (var e = 0; e < entries.length; e++) {
          if (entries[e].isIntersecting) {
            section.dataset.wshInView = 'true';
            var activeWrap = section.querySelector('.wsh-col-video__video-wrap[data-active="true"]');
            if (activeWrap) {
              var video = activeWrap.querySelector('video');
              if (video && video.preload === 'none') {
                video.preload = 'metadata';
              }
            }
            observer.disconnect();
            break;
          }
        }
      }, { threshold: 0.1 });

      observer.observe(section);
    }

    // Initialize label band + dots for the default active state
    var activeWrap = section.querySelector('.wsh-col-video__video-wrap[data-active="true"]');
    if (activeWrap) {
      updateLabelBand(activeWrap);
      renderDots(activeWrap.dataset.genre);
    }
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init on section re-render
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector(SECTION_SEL)) {
      var s = e.target.querySelector(SECTION_SEL);
      if (s) s.dataset.wshInited = '';
      init();
    }
  });
})();
